import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useIntro } from '@/components/landing/intro-context'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import { gsap, ScrollTrigger } from '@/lib/gsap'

const MIN_TIME = 1400
const MAX_WAIT = 8000

// Holds the MACHGO mark on black until the window artwork and the fonts are ready,
// then lifts away and hands over to the hero intro.
const Preloader: FC = (): ReactNode => {
    const { windowReady, startIntro } = useIntro()
    const { start } = useSmoothScroll()
    const [fontsReady, setFontsReady] = useState(false)
    const [minTimePassed, setMinTimePassed] = useState(false)
    const [forced, setForced] = useState(false)
    const [done, setDone] = useState(false)
    const overlayRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const barRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        let cancelled = false

        if (document.fonts?.ready) {
            document.fonts.ready.then(() => {
                if (!cancelled) setFontsReady(true)
            })
        } else {
            setFontsReady(true)
        }

        const minTimer = window.setTimeout(() => setMinTimePassed(true), MIN_TIME)
        const maxTimer = window.setTimeout(() => setForced(true), MAX_WAIT)

        return () => {
            cancelled = true
            window.clearTimeout(minTimer)
            window.clearTimeout(maxTimer)
        }
    }, [])

    const progress = 0.08 + (fontsReady ? 0.22 : 0) + (windowReady ? 0.5 : 0) + (minTimePassed ? 0.2 : 0)
    const complete = forced || (fontsReady && windowReady && minTimePassed)

    useEffect(() => {
        if (!barRef.current || complete) return
        gsap.to(barRef.current, { scaleX: progress, duration: 0.8, ease: 'power2.out' })
    }, [progress, complete])

    useEffect(() => {
        if (!complete) return

        const timeline = gsap.timeline({ onComplete: () => setDone(true) })

        timeline
            .to(barRef.current, { scaleX: 1, duration: 0.5, ease: 'power2.out' })
            .to(trackRef.current, { autoAlpha: 0, duration: 0.4 }, '+=0.15')
            .add(() => {
                start()
                startIntro()
                // The scrollbar returns once scrolling is released, so measure again after it does
                window.requestAnimationFrame(() => ScrollTrigger.refresh())
            })
            .to(overlayRef.current, { autoAlpha: 0, duration: 1.4, ease: 'power2.inOut' }, '<')

        return () => {
            timeline.kill()
        }
    }, [complete, startIntro, start])

    if (done) return null

    return (
        <div
            ref={overlayRef}
            role='status'
            aria-label='Loading MACHGO'
            className='fixed inset-0 z-[55] bg-obsidian'
        >
            <div
                ref={trackRef}
                className='absolute left-1/2 top-[calc(50%+2.75rem)] -ml-20 h-px w-40 overflow-hidden bg-champagne/20'
            >
                <div ref={barRef} className='h-full w-full origin-left bg-champagne' style={{ transform: 'scaleX(0.08)' }} />
            </div>
        </div>
    )
}

export default Preloader
